export const PRINTER_STORAGE_KEY = 'packer_print_printer_v1';
export const PRINT_AFTER_PRODUCTION_KEY = 'packer_print_after_production_v1';
export const MS_BARCODE_FORMAT_KEY = 'packer_ms_barcode_format_v1';
export const MS_BARCODE_FORMATS = [
  { id: '58x40', label: '58×40' },
  { id: '75x120', label: '75×120' },
];
export const DEFAULT_MS_BARCODE_FORMAT = '58x40';

export const PRINT_STATUS = {
  queued: { label: 'В очереди', color: 'default' },
  leased: { label: 'Передаётся агенту', color: 'info' },
  spooled: { label: 'Передано на принтер', color: 'info' },
  completed: { label: 'Подтверждено агентом', color: 'success' },
  failed: { label: 'Ошибка печати', color: 'error' },
  unknown: { label: 'Статус неизвестен', color: 'warning' },
  canceled: { label: 'Отменено', color: 'default' },
};

export function normalizeStoredPrinter(value) {
  if (!value || typeof value !== 'object') return null;
  const uid = String(value.printer_uid || '').trim();
  if (!uid || /[\u0000-\u001f]/.test(uid)) return null;
  return {
    printer_uid: uid,
    label: String(value.label || value.name || uid).trim() || uid,
  };
}

export function readStoredPrinter(storage = globalThis.localStorage) {
  if (!storage) return null;
  try {
    return normalizeStoredPrinter(JSON.parse(storage.getItem(PRINTER_STORAGE_KEY)));
  } catch {
    return null;
  }
}

export function writeStoredPrinter(printer, storage = globalThis.localStorage) {
  if (!storage) return null;
  const normalized = normalizeStoredPrinter(printer);
  if (!normalized) {
    storage.removeItem(PRINTER_STORAGE_KEY);
    return null;
  }
  storage.setItem(PRINTER_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export function readPrintAfterProduction(storage = globalThis.localStorage) {
  if (!storage) return false;
  try {
    const raw = String(storage.getItem(PRINT_AFTER_PRODUCTION_KEY) || '').trim().toLowerCase();
    return raw === '1' || raw === 'true';
  } catch {
    return false;
  }
}

export function writePrintAfterProduction(enabled, storage = globalThis.localStorage) {
  if (!storage) return false;
  const next = Boolean(enabled);
  storage.setItem(PRINT_AFTER_PRODUCTION_KEY, next ? '1' : '0');
  return next;
}

export function normalizeMsBarcodeFormat(value) {
  const raw = String(value || '').trim().toLowerCase().replace(/×/g, 'x').replace(/\s+/g, '');
  return MS_BARCODE_FORMATS.some((item) => item.id === raw) ? raw : DEFAULT_MS_BARCODE_FORMAT;
}

export function readMsBarcodeFormat(storage = globalThis.localStorage) {
  if (!storage) return DEFAULT_MS_BARCODE_FORMAT;
  try {
    return normalizeMsBarcodeFormat(storage.getItem(MS_BARCODE_FORMAT_KEY));
  } catch {
    return DEFAULT_MS_BARCODE_FORMAT;
  }
}

export function writeMsBarcodeFormat(format, storage = globalThis.localStorage) {
  const next = normalizeMsBarcodeFormat(format);
  if (!storage) return next;
  storage.setItem(MS_BARCODE_FORMAT_KEY, next);
  return next;
}

export function resolvePrinterSelection(printers, stored) {
  const list = Array.isArray(printers) ? printers : [];
  const normalized = normalizeStoredPrinter(stored);
  if (!normalized) return { selected: null, unavailable: false };
  const selected = list.find((item) => item.printer_uid === normalized.printer_uid) || null;
  return { selected, unavailable: !selected };
}

export function printStatusMeta(status) {
  return PRINT_STATUS[String(status || '')] || { label: status || 'Неизвестно', color: 'default' };
}

/** WB FBS: стикер доступен после confirm/complete в ЛК. */
export function taskHasPrintableSticker(task) {
  const supplierStatus = String(task?.supplier_status || '').trim().toLowerCase();
  return supplierStatus === 'confirm' || supplierStatus === 'complete';
}

/**
 * Можно ли печатать этикетку строки (WB / Ozon / Yandex) в едином АРМ.
 * Ozon/Yandex: capabilities.can_print_sticker / label_* / READY_TO_SHIP.
 * WB: sticker в БД или supplier confirm/complete (очередь сама тянет PNG из WB).
 */
export function taskCanPrintLabel(task) {
  if (!task || typeof task !== 'object') return false;
  if (task.capabilities?.can_print_sticker === true) return true;
  const mp = String(task.marketplace || '').toLowerCase();
  const isYandex = mp === 'yandex' || mp === '3' || Boolean(task.yandex_order_id);
  const isOzon = mp === 'ozon' || mp === '2'
    || (task.posting_number && !task.wb_order_id && !task.yandex_order_id);
  if (isYandex) {
    // Старый stub capabilities.can_print_sticker=false не должен глушить READY_TO_SHIP / label_*.
    if (task.label_ready_at || task.label_printed_at || task.label_applied_at || task.label_file) {
      return true;
    }
    return String(task.ya_substatus || '').toUpperCase() === 'READY_TO_SHIP';
  }
  if (isOzon) {
    if (task.capabilities?.can_print_sticker === false) return false;
    return Boolean(task.label_ready_at || task.label_printed_at || task.label_applied_at || task.label_file);
  }
  if (task.sticker_barcode || task.sticker_number) return true;
  return taskHasPrintableSticker(task);
}

export function taskSourceId(task) {
  const id = String(
    task?.external_id
    ?? task?.wb_order_id
    ?? task?.yandex_order_id
    ?? task?.posting_number
    ?? task?.source_id
    ?? '',
  ).trim();
  return id;
}

export function summarizePrintBatch(jobs) {
  const list = Array.isArray(jobs) ? jobs : [];
  const counts = {};
  list.forEach((job) => {
    const status = String(job?.status || 'unknown');
    counts[status] = (counts[status] || 0) + 1;
  });
  return {
    total: list.length,
    counts,
    terminal: list.filter((job) => ['completed', 'failed', 'unknown', 'canceled'].includes(job?.status)).length,
  };
}

export function groupPrintBatches(jobs) {
  const groups = new Map();
  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    const key = String(job?.batch_id || `job-${job?.id || groups.size}`);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(job);
  });
  return [...groups.entries()].map(([batchId, batchJobs]) => ({
    batchId,
    jobs: batchJobs,
    ...summarizePrintBatch(batchJobs),
  }));
}

export function orderSelectedSourceIds(rows, selectedIds) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const ordered = [];
  const seen = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = taskSourceId(row);
    if (id && selected.has(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  });
  // Сохраняем выбранные на других страницах после видимых строк в стабильном
  // порядке Set, не теряя ни одного source_id.
  selected.forEach((value) => {
    const id = String(value ?? '').trim();
    if (id && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  });
  return ordered;
}

export function productPrintGroupKey(task = {}) {
  const nmId = String(task.nm_id ?? task.product?.product_id ?? '').trim();
  if (nmId) return `nm:${nmId}`;
  const barcode = String(task.barcode ?? '').trim();
  if (barcode) return `barcode:${barcode}`;
  const article = String(task.product?.article ?? task.article ?? '').trim().toLowerCase();
  return article ? `article:${article}` : null;
}

export function buildGroupedStickerDocuments(rows, selectedIds) {
  const orderedIds = orderSelectedSourceIds(rows, selectedIds);
  const rowsById = new Map((Array.isArray(rows) ? rows : []).map((row) => [
    taskSourceId(row),
    row,
  ]).filter(([id]) => id));
  const groups = new Map();
  orderedIds.forEach((sourceId) => {
    const row = rowsById.get(sourceId);
    // Выбор между страницами сохраняется. Если данных строки уже нет в
    // текущем ответе, не объединяем её вслепую с другим товаром.
    const key = productPrintGroupKey(row) || `order:${sourceId}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(sourceId);
  });

  const documents = [];
  groups.forEach((sourceIds) => {
    documents.push({
      document_type: 'product_technical_label',
      source_id: sourceIds[0],
    });
    sourceIds.forEach((sourceId) => {
      documents.push({
        document_type: 'wb_order_sticker',
        source_id: sourceId,
      });
    });
  });
  return {
    documents,
    stickerCount: orderedIds.length,
    technicalLabelCount: groups.size,
  };
}
